import { useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// 全局标记，用于允许特定操作绕过页面离开确认
let allowNavigationFlag = false;

/**
 * 设置允许导航标记，用于登出等操作绕过页面离开确认
 */
export const setAllowNavigation = (allow: boolean = true): void => {
    allowNavigationFlag = allow;
    
    // 设置一个超时，自动重置标记，防止标记永久生效
    if (allow) {
        setTimeout(() => {
            allowNavigationFlag = false;
        }, 1000); // 1秒后自动重置
    }
};

/**
 * 自定义Hook，用于禁用页面的边缘滑动返回手势和虚拟按键返回
 * @param shouldPrevent 是否应该禁用返回操作（可选，默认为true）
 * @param options 配置选项（可选）
 * @returns void
 */
export const usePreventEdgeSwipe = (
    shouldPrevent: boolean = true,
    options: {
        edgeWidth?: number; // 边缘宽度（像素）
        onlyCurrentPath?: boolean; // 是否只在当前路径下禁用
        targetPath?: string; // 指定要禁用的路径，如果不提供则使用当前路径
        enableExitPrompt?: boolean; // 是否启用退出提示（连续点击两次返回键退出）
        exitPromptTimeout?: number; // 退出提示超时时间（毫秒）
        exitCallback?: () => void; // 退出回调函数
    } = {}
) => {
    const navigate = useNavigate();
    const location = useLocation();
    const originalHistoryLengthRef = useRef<number>(0);
    const exitAttemptRef = useRef<boolean>(false);
    const exitTimeoutRef = useRef<number | null>(null);
    
    // 默认选项
    const defaultOptions = {
        edgeWidth: 15,
        onlyCurrentPath: true,
        targetPath: location.pathname,
        enableExitPrompt: false,
        exitPromptTimeout: 2000,
        exitCallback: undefined
    };
    
    // 合并选项
    const mergedOptions = { ...defaultOptions, ...options };
    
    // 处理退出尝试
    const handleExitAttempt = useCallback(() => {
        // 如果设置了允许导航标记，直接允许导航
        if (allowNavigationFlag) {
            return false; // 不阻止导航
        }
        
        if (mergedOptions.enableExitPrompt) {
            if (!exitAttemptRef.current) {
                exitAttemptRef.current = true;
                
                // 显示提示（可以由调用者自定义处理）
                if (mergedOptions.exitCallback) {
                    mergedOptions.exitCallback();
                }
                
                // 设置超时，重置退出尝试状态
                if (exitTimeoutRef.current) {
                    window.clearTimeout(exitTimeoutRef.current);
                }
                
                exitTimeoutRef.current = window.setTimeout(() => {
                    exitAttemptRef.current = false;
                    exitTimeoutRef.current = null;
                }, mergedOptions.exitPromptTimeout);
                
                return true; // 第一次尝试退出
            } else {
                // 第二次尝试退出，允许导航
                exitAttemptRef.current = false;
                if (exitTimeoutRef.current) {
                    window.clearTimeout(exitTimeoutRef.current);
                    exitTimeoutRef.current = null;
                }
                return false; // 允许退出
            }
        }
        
        return true; // 默认阻止退出
    }, [mergedOptions]);
    
    useEffect(() => {
        // 如果不需要禁用，直接返回
        if (!shouldPrevent) {
            return;
        }
        
        // 存储当前历史长度，用于后续检测
        originalHistoryLengthRef.current = window.history.length;
        
        // 检测是否为移动设备
        const isMobileDevice = () => {
            return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        };
        
        if (!isMobileDevice()) {
            return; // 如果不是移动设备，不执行后续代码
        }
        
        // 如果设置了onlyCurrentPath，检查当前路径是否匹配目标路径
        if (mergedOptions.onlyCurrentPath && location.pathname !== mergedOptions.targetPath) {
            return;
        }
        
        // 检测是否为iOS设备
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        const isAndroid = /Android/i.test(navigator.userAgent);
        
        // 插入一个假的历史记录状态，让用户必须滑动两次才能离开
        window.history.pushState({ noExit: true }, '', location.pathname);
        
        // 对于iOS设备，使用更温和的边缘滑动拦截
        if (isIOS) {
            // 添加全屏覆盖层在左右边缘来拦截iOS的边缘滑动，但使用透明元素
            const leftEdge = document.createElement('div');
            leftEdge.style.position = 'fixed';
            leftEdge.style.top = '0';
            leftEdge.style.left = '0';
            leftEdge.style.width = `${mergedOptions.edgeWidth}px`;
            leftEdge.style.height = '100%';
            leftEdge.style.zIndex = '9999';
            leftEdge.style.backgroundColor = 'transparent'; // 确保完全透明
            leftEdge.style.pointerEvents = 'auto'; // 确保可以捕获事件
            
            const rightEdge = document.createElement('div');
            rightEdge.style.position = 'fixed';
            rightEdge.style.top = '0';
            rightEdge.style.right = '0';
            rightEdge.style.width = `${mergedOptions.edgeWidth}px`;
            rightEdge.style.height = '100%';
            rightEdge.style.zIndex = '9999';
            rightEdge.style.backgroundColor = 'transparent'; // 确保完全透明
            rightEdge.style.pointerEvents = 'auto'; // 确保可以捕获事件
            
            document.body.appendChild(leftEdge);
            document.body.appendChild(rightEdge);
            
            // 只处理开始的触摸事件，判断是否是边缘滑动手势
            let isSwiping = false;
            let startX = 0;
            
            const handleTouchStart = (e: TouchEvent) => {
                if (e.touches && e.touches.length > 0) {
                    startX = e.touches[0].clientX;
                    // 只有在边缘开始触摸时才可能是滑动手势
                    isSwiping = startX < 10 || startX > window.innerWidth - 10;
                    
                    if (isSwiping) {
                        e.preventDefault();
                    }
                }
            };
            
            const handleTouchMove = (e: TouchEvent) => {
                // 只有识别为滑动手势时才阻止
                if (isSwiping) {
                    e.preventDefault();
                }
            };
            
            const handleTouchEnd = () => {
                isSwiping = false;
            };
            
            leftEdge.addEventListener('touchstart', handleTouchStart, { passive: false });
            leftEdge.addEventListener('touchmove', handleTouchMove, { passive: false });
            leftEdge.addEventListener('touchend', handleTouchEnd);
            
            rightEdge.addEventListener('touchstart', handleTouchStart, { passive: false });
            rightEdge.addEventListener('touchmove', handleTouchMove, { passive: false });
            rightEdge.addEventListener('touchend', handleTouchEnd);
            
            // 清理函数
            return () => {
                document.body.removeChild(leftEdge);
                document.body.removeChild(rightEdge);
            };
        }
        
        // 对于Android或其他设备，使用更温和的事件监听方式
        let startX = 0;
        let startY = 0;
        let isEdgeTouch = false;
        
        const handleTouchStart = (e: TouchEvent) => {
            if (!e.touches || e.touches.length === 0) return;
            
            const touch = e.touches[0];
            startX = touch.clientX;
            startY = touch.clientY;
            
            // 只检测非常靠近边缘的触摸
            isEdgeTouch = startX < mergedOptions.edgeWidth || startX > window.innerWidth - mergedOptions.edgeWidth;
            
            // 不阻止默认行为，只记录开始位置
        };
        
        const handleTouchMove = (e: TouchEvent) => {
            if (!isEdgeTouch || !e.touches || e.touches.length === 0) return;
            
            const touch = e.touches[0];
            const deltaX = touch.clientX - startX;
            const deltaY = touch.clientY - startY;
            
            // 只有在水平移动大于垂直移动，且是从边缘开始的触摸才视为边缘滑动
            if (Math.abs(deltaX) > Math.abs(deltaY)) {
                // 从左向右滑动(正值)，或从右向左滑动(负值)
                if ((startX < mergedOptions.edgeWidth && deltaX > 0) || 
                    (startX > window.innerWidth - mergedOptions.edgeWidth && deltaX < 0)) {
                    e.preventDefault();
                }
            }
        };
        
        // 禁用历史状态变化 - 增强版，处理所有返回操作，包括虚拟按键
        const lockHistoryNavigation = () => {
            // 监听历史状态变化
            const handlePopState = (e: PopStateEvent) => {
                // 处理退出尝试
                const shouldPreventExit = handleExitAttempt();
                
                if (shouldPreventExit) {
                    // 阻止导航并返回当前页面
                    navigate(location.pathname, { replace: true });
                    
                    // 重新插入历史状态
                    setTimeout(() => {
                        window.history.pushState({ noExit: true }, '', location.pathname);
                    }, 0);
                    
                    return false;
                }
                
                // 如果不阻止退出，则允许导航
                return true;
            };
            
            window.addEventListener('popstate', handlePopState);
            
            // 返回清理函数
            return () => {
                window.removeEventListener('popstate', handlePopState);
            };
        };
        
        // 注册事件监听，使用捕获但保持passive为true以提高性能
        document.addEventListener('touchstart', handleTouchStart, { passive: true });
        document.addEventListener('touchmove', handleTouchMove, { passive: false });
        document.addEventListener('touchend', () => { isEdgeTouch = false; });
        
        // 锁定历史导航
        const unlockHistoryNavigation = lockHistoryNavigation();
        
        // Android 物理返回键和虚拟按键处理
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Backspace' || e.key === 'Escape') {
                e.preventDefault();
                handleExitAttempt();
                return false;
            }
        };
        
        document.addEventListener('keydown', handleKeyDown);
        
        // Android 特定的返回键处理
        if (isAndroid && 'cordova' in window) {
            // 如果在Cordova环境中
            document.addEventListener('backbutton', (e) => {
                e.preventDefault();
                handleExitAttempt();
            }, false);
        }
        
        // 处理浏览器导航按钮点击
        if (navigator.userAgent.includes('Chrome')) {
            // 对于Chrome浏览器，额外添加一个处理
            window.addEventListener('beforeunload', (e) => {
                // 如果设置了允许导航标记，不阻止页面离开
                if (allowNavigationFlag) {
                    return;
                }
                
                if (handleExitAttempt()) {
                    e.preventDefault();
                    e.returnValue = '';
                    return '';
                }
            });
        }
        
        // 清理函数
        return () => {
            document.removeEventListener('touchstart', handleTouchStart);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', () => { isEdgeTouch = false; });
            document.removeEventListener('keydown', handleKeyDown);
            if (isAndroid && 'cordova' in window) {
                document.removeEventListener('backbutton', () => {});
            }
            if (navigator.userAgent.includes('Chrome')) {
                window.removeEventListener('beforeunload', () => {});
            }
            if (unlockHistoryNavigation) {
                unlockHistoryNavigation();
            }
            if (exitTimeoutRef.current) {
                window.clearTimeout(exitTimeoutRef.current);
            }
        };
    }, [shouldPrevent, location, navigate, mergedOptions, handleExitAttempt]);
};

export default usePreventEdgeSwipe;